import React from "react";
import { Card, ListGroup } from "react-bootstrap";
import "./ProfitLossRatio.scss";

// Normalize many date shapes into "YYYY-MM-DD" (local)
const toDayKey = (raw) => {
  if (raw == null) return null;

  // Date object
  if (raw instanceof Date) return ymdFromDate(raw);

  // Number: could be YYYYMMDDHHmmss / YYYYMMDD or epoch seconds/ms
  if (typeof raw === "number") {
    const sNum = String(raw);
    if (/^\d+$/.test(sNum)) {
      if (sNum.length === 14) {
        const y = +sNum.slice(0, 4);
        const m = +sNum.slice(4, 6) - 1;
        const d = +sNum.slice(6, 8);
        const H = +sNum.slice(8, 10);
        const M = +sNum.slice(10, 12);
        const S = +sNum.slice(12, 14);
        return ymdFromDate(new Date(y, m, d, H, M, S));
      }
      if (sNum.length === 8) {
        const y = +sNum.slice(0, 4);
        const m = +sNum.slice(4, 6) - 1;
        const d = +sNum.slice(6, 8);
        return ymdFromDate(new Date(y, m, d));
      }
    }
    // Fallback: assume epoch (seconds or ms)
    const ms = raw < 1e12 ? raw * 1000 : raw;
    return ymdFromDate(new Date(ms));
  }

  const s = String(raw).trim();

  // Digits: YYYYMMDD or YYYYMMDDHHmmss (string)
  if (/^\d+$/.test(s)) {
    if (s.length === 8) {
      const y = +s.slice(0, 4);
      const m = +s.slice(4, 6) - 1;
      const d = +s.slice(6, 8);
      return ymdFromDate(new Date(y, m, d));
    }
    if (s.length === 14) {
      const y = +s.slice(0, 4);
      const m = +s.slice(4, 6) - 1;
      const d = +s.slice(6, 8);
      const H = +s.slice(8, 10);
      const M = +s.slice(10, 12);
      const S = +s.slice(12, 14);
      return ymdFromDate(new Date(y, m, d, H, M, S));
    }
  }

  // Fallback: let Date parse (ISO string, etc.)
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : ymdFromDate(dt);
};

const ymdFromDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const calculatePnLStatistics = (trades) => {
  if (!trades || !trades.length) {
    return {
      totalProfit: "0.00",
      totalLoss: "0.00",
      profitLossRatio: "0.00",
      winRate: "0.00",
      lossRate: "0.00",
      breakevenRate: "0.00",
      avgProfit: "0.00",
      avgLoss: "0.00",
      biggestProfit: "0.00",
      biggestLoss: "0.00",
      medianProfit: "0.00",
      medianLoss: "0.00",
      largestProfitDayAmount: "0.00",
      largestLossDayAmount: "0.00",
    };
  }

  let totalProfit = 0;
  let totalLoss = 0;
  let biggestProfit = 0; // positive magnitude
  let biggestLoss = 0; // positive magnitude
  let profitableTradesCount = 0;
  let unprofitableTradesCount = 0;
  let breakevenTradesCount = 0;

  const closedProfits = [];
  const closedLosses = [];

  // Aggregate P&L by calendar day (signed)
  const dailyMap = new Map(); // key: "YYYY-MM-DD" -> sum

  for (const trade of trades) {
    const pnl = Number(trade.pnl) || 0;

    // Use closedDate primarily (your data has YYYYMMDDHHmmss), with fallbacks
    const rawDate = trade.closedDate ?? trade.date ?? trade.tradeDate ?? null;
    const dayKey = toDayKey(rawDate);
    if (dayKey) {
      dailyMap.set(dayKey, (dailyMap.get(dayKey) ?? 0) + pnl);
    }

    if (pnl > 0) {
      totalProfit += pnl;
      closedProfits.push(pnl);
      profitableTradesCount++;
      if (pnl > biggestProfit) biggestProfit = pnl;
    } else if (pnl < 0) {
      const absPnl = Math.abs(pnl);
      totalLoss += absPnl;
      closedLosses.push(absPnl);
      unprofitableTradesCount++;
      if (absPnl > biggestLoss) biggestLoss = absPnl;
    } else {
      breakevenTradesCount++;
    }
  }

  // Medians
  closedProfits.sort((a, b) => a - b);
  closedLosses.sort((a, b) => a - b);
  const medianOf = (arr) =>
    arr.length
      ? (arr[Math.floor(arr.length / 2)] + arr[Math.ceil(arr.length / 2) - 1]) /
        2
      : 0;

  const medianProfit = medianOf(closedProfits);
  const medianLoss = medianOf(closedLosses);

  // Rates
  const totalTrades = trades.length;
  const winRate = (profitableTradesCount / totalTrades) * 100;
  const lossRate = (unprofitableTradesCount / totalTrades) * 100;
  const breakevenRate = (breakevenTradesCount / totalTrades) * 100;

  // Averages
  const avgProfit =
    profitableTradesCount > 0 ? totalProfit / profitableTradesCount : 0;
  const avgLoss =
    unprofitableTradesCount > 0 ? totalLoss / unprofitableTradesCount : 0;

  // P/L ratio
  let profitLossRatio;
  if (totalLoss === 0) {
    profitLossRatio = totalProfit > 0 ? "∞" : "0.00";
  } else {
    profitLossRatio = (totalProfit / totalLoss).toFixed(2);
  }

  // Largest profit day / largest loss day (by summed day P&L)
  let largestProfitDaySum = 0; // keep as positive if > 0
  let largestLossDaySum = 0; // keep as negative if < 0

  for (const [, sum] of dailyMap) {
    if (sum > largestProfitDaySum) largestProfitDaySum = sum;
    if (sum < largestLossDaySum) largestLossDaySum = sum;
  }

  return {
    totalProfit: totalProfit.toFixed(2),
    totalLoss: totalLoss.toFixed(2),
    profitLossRatio,
    winRate: winRate.toFixed(2),
    lossRate: lossRate.toFixed(2),
    breakevenRate: breakevenRate.toFixed(2),
    avgProfit: avgProfit.toFixed(2),
    avgLoss: avgLoss.toFixed(2),
    biggestProfit: biggestProfit.toFixed(2),
    biggestLoss: biggestLoss.toFixed(2),
    medianProfit: medianProfit.toFixed(2),
    medianLoss: medianLoss.toFixed(2),

    // New fields: amounts only
    largestProfitDayAmount: Math.max(0, largestProfitDaySum).toFixed(2),
    largestLossDayAmount: Math.abs(Math.min(0, largestLossDaySum)).toFixed(2),
  };
};

const ProfitLossRatio = ({ trades }) => {
  const stats = calculatePnLStatistics(trades);

  return (
    <Card className="profitLossRatio tradeCard">
      <Card.Header className="profitLossRatioHeader">
        Profit/Loss Ratio
      </Card.Header>
      <Card.Body>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <strong>P&L Ratio: </strong>
            {stats.profitLossRatio}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Win Rate: </strong>
            <span className="profit">{stats.winRate}%</span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Breakeven Rate: </strong>
            <span>{stats.breakevenRate}%</span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Loss Rate: </strong>
            <span className="loss">{stats.lossRate}%</span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Avg. Gain: </strong>
            <span className="profit">
              ${parseFloat(stats.avgProfit).toLocaleString()}
            </span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Avg. Loss: </strong>
            <span className="loss">
              ${parseFloat(stats.avgLoss).toLocaleString()}
            </span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Median Gain: </strong>
            <span className="profit">
              ${parseFloat(stats.medianProfit).toLocaleString()}
            </span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Median Loss: </strong>
            <span className="loss">
              ${parseFloat(stats.medianLoss).toLocaleString()}
            </span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Biggest Gain: </strong>
            <span className="profit">
              ${parseFloat(stats.biggestProfit).toLocaleString()}
            </span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Biggest Loss: </strong>
            <span className="loss">
              ${parseFloat(stats.biggestLoss).toLocaleString()}
            </span>
          </ListGroup.Item>

          {/* New rows: daily aggregated extremes */}
          <ListGroup.Item>
            <strong>Biggest Profit (Day): </strong>
            <span className="profit">
              ${parseFloat(stats.largestProfitDayAmount).toLocaleString()}
            </span>
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Biggest Loss (Day): </strong>
            <span className="loss">
              ${parseFloat(stats.largestLossDayAmount).toLocaleString()}
            </span>
          </ListGroup.Item>
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export default ProfitLossRatio;
